/**
 * TenantsListPage — Indice globale tenant per Operator Console super_admin.
 *
 * Estetica editoriale (Fraunces display + Manrope body + JetBrains Mono):
 *  - Header con titolo display in font serif e contatore mono
 *  - Tabella "flat" senza shadows pesanti, righe alte e tipografia gerarchica
 *  - Colonne MOD-5: Tier badge a colori, Seat bar, Billing chip, MRR mono
 *  - Hover row: leggera washing in bone, transizione 120ms
 *
 * Pattern UX: Stripe Dashboard list view — denso ma respiratorio, dati
 * numerici tabulari per scan veloce.
 *
 * @since 2026-05-18 (Editorial design MOD-2.5)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Stack, CircularProgress, Alert,
  InputAdornment, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button, FormControlLabel, RadioGroup, Radio,
  Snackbar,
} from '@mui/material';
import {
  Refresh, Visibility, Login as ImpersonateIcon, Search,
  DeleteOutline as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { listTenants, type TenantConsoleListItem } from '../services/tenantService';
import { useListiniDelCanone } from '../services/listinoDeiPiani';
import {
  canoneMensileDellAzienda, incassoDellAzienda, type CanoneMensile, type IncassoDellAzienda, type ListiniDelCanone,
} from '@shared/abbonamento/canoneMensile';
import { naturaDellAzienda } from '@shared/abbonamento/naturaAzienda';
import { DettaglioCanone, euroDaCentesimi, testoIncasso } from '../components/canone/DettaglioCanone';
import { startImpersonation } from '../services/impersonationService';
import { functions } from '../firebase';
import { SoftChip } from '../components/SoftChip';
import { TierBadge } from '../components/design/TierBadge';
import { SeatBar } from '../components/design/SeatBar';
import { tokens } from '../theme';
import type { TenantTier, OnboardingStatus } from '@shared/types';
import { InterruttoreVista } from '../components/mappa/InterruttoreVista';

// ============================================================================
// MAPPINGS UI
// ============================================================================

const TIER_FILTER_OPTIONS: Array<{ value: TenantTier | 'all'; label: string }> = [
  { value: 'all', label: 'Tutti i tier' },
  { value: 'free', label: 'Free' },
  { value: 'base', label: 'Base' },
  { value: 'pro', label: 'Pro' },
  { value: 'business', label: 'Business' },
];

const STATUS_TONE = {
  pending_email: 'warning',
  active_limited: 'info',
  active_full: 'success',
  suspended: 'error',
} as const;

const STATUS_LABELS: Record<OnboardingStatus, string> = {
  pending_email: 'Pending email',
  active_limited: 'Active limited',
  active_full: 'Active full',
  suspended: 'Suspended',
};

const BILLING_STATUS_TONE: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  canceled: 'error',
  free: 'default',
};

const BILLING_STATUS_LABELS: Record<string, string> = {
  active: 'Pagante',
  trialing: 'In prova',
  past_due: 'Insoluto',
  canceled: 'Cancellato',
  free: 'Gratuito',
};

// ============================================================================
// MRR STIMA = il canone mensile COMPLETO (posti + app a persona + strumenti a canone), IVA esclusa, dalla regola condivisa
// `@shared/abbonamento/canoneMensile`, la stessa della scheda «Billing & Stripe». Fino al 02/11/2026 contava solo i posti.
// ============================================================================

/**
 * Il canone al mese di un'azienda: null se esente o dimostrativa (non contribuiscono) o se i listini non sono ancora
 * letti.
 */
function canoneDellAzienda(tenant: TenantConsoleListItem, listini: ListiniDelCanone | null, oraMs: number): CanoneMensile | null {
  if (tenant.billing?.exempt || naturaDellAzienda(tenant) === 'dimostrativa') return null;
  if (!listini) return null;
  return canoneMensileDellAzienda(tenant, listini, oraMs);
}

function formatEUR(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

// ============================================================================
// COMPONENT
// ============================================================================

type TenantStatus = 'active' | 'grace_period' | 'restricted' | 'suspended' | 'canceled' | 'deleted';

interface DeleteDialogState {
  open: boolean;
  tenant: TenantConsoleListItem | null;
  mode: 'soft' | 'hard';
  confirmationName: string;
  submitting: boolean;
  error: string | null;
}

const INITIAL_DELETE_STATE: DeleteDialogState = {
  open: false, tenant: null, mode: 'soft', confirmationName: '', submitting: false, error: null,
};

export const TenantsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantConsoleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState<TenantTier | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<OnboardingStatus | 'all'>('all');
  // Filtro tenant_status (lifecycle), default 'only_active' (nasconde suspended/canceled)
  const [filterLifecycle, setFilterLifecycle] = useState<'only_active' | 'all' | TenantStatus>('only_active');
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>(INITIAL_DELETE_STATE);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  // I listini veri (piani, app, strumenti): finché non ci sono, la spesa resta «—» invece di un numero a metà.
  const { listini } = useListiniDelCanone('TenantsListPage');
  // Il canone di ogni azienda e se entra davvero in cassa, calcolati una volta (servono all'intestazione e alla riga).
  // L'ora decide i regali scaduti e le prove finite.
  const canoni = useMemo(() => {
    const oraMs = Date.now();
    return new Map(tenants.map((t) => [t.id, {
      canone: canoneDellAzienda(t, listini, oraMs),
      incasso: incassoDellAzienda(t, oraMs),
    }]));
  }, [tenants, listini]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listTenants();
      setTenants(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento tenant');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return tenants.filter((t) => {
      // Filtro lifecycle (default 'only_active' nasconde suspended/canceled/deleted)
      const lifecycleStatus = (t as any).tenant_status || 'active';
      if (filterLifecycle === 'only_active') {
        if (lifecycleStatus === 'suspended' || lifecycleStatus === 'canceled' || lifecycleStatus === 'deleted') return false;
      } else if (filterLifecycle !== 'all' && lifecycleStatus !== filterLifecycle) {
        return false;
      }
      if (filterTier !== 'all' && t.tier !== filterTier) return false;
      if (filterStatus !== 'all' && t.onboarding_status !== filterStatus) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack = [t.nome_azienda, t.partita_iva, t.email_aziendale, t.id]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [tenants, search, filterTier, filterStatus, filterLifecycle]);

  const handleOpenDeleteDialog = (tenant: TenantConsoleListItem) => {
    setDeleteDialog({ ...INITIAL_DELETE_STATE, open: true, tenant });
  };

  const handleDelete = async () => {
    if (!deleteDialog.tenant) return;
    if (deleteDialog.mode === 'hard' && deleteDialog.confirmationName.trim() !== deleteDialog.tenant.nome_azienda.trim()) {
      setDeleteDialog((s) => ({ ...s, error: `Scrivi esattamente "${deleteDialog.tenant!.nome_azienda}" per confermare` }));
      return;
    }
    setDeleteDialog((s) => ({ ...s, submitting: true, error: null }));
    try {
      const fn = httpsCallable(functions, 'deleteTenant');
      const result = await fn({
        tenant_id: deleteDialog.tenant.id,
        mode: deleteDialog.mode,
        confirmation_name: deleteDialog.mode === 'hard' ? deleteDialog.confirmationName : undefined,
      });
      const data = result.data as { mode: string; tenant_name: string; documents_deleted?: number; auth_users_deleted?: number };
      setSnack({
        msg: data.mode === 'hard'
          ? `Tenant "${data.tenant_name}" eliminato definitivamente (${data.documents_deleted} doc, ${data.auth_users_deleted} utenti)`
          : `Tenant "${data.tenant_name}" sospeso. Dati intatti, reversibile.`,
        severity: 'success',
      });
      setDeleteDialog(INITIAL_DELETE_STATE);
      await load();
    } catch (e: any) {
      setDeleteDialog((s) => ({ ...s, submitting: false, error: e?.message || 'Errore eliminazione' }));
    }
  };

  // Aggregati header (live)
  // Due somme diverse: ciò che entra in cassa ogni mese (abbonamenti che pagano) e ciò che entrerebbe alla fine delle
  // prove gratuite. Ritardi, pause e aziende senza abbonamento non stanno in nessuna delle due.
  const aggregates = useMemo(() => {
    let incassatoMRR = 0;
    let inProvaMRR = 0;
    let payingCount = 0;
    let trialCount = 0;
    let exemptCount = 0;
    for (const t of tenants) {
      const voce = canoni.get(t.id);
      const euro = (voce?.canone?.totale_centesimi ?? 0) / 100;
      if (voce?.incasso.stato === 'paga') { payingCount += 1; incassatoMRR += euro; }
      if (voce?.incasso.stato === 'in_prova') { trialCount += 1; inProvaMRR += euro; }
      // Un'azienda dimostrativa non è un cliente: non conta fra gli esenti (che sono aziende vere che non pagano).
      if (t.billing?.exempt && voce?.incasso.stato !== 'dimostrativa') exemptCount += 1;
    }
    return { incassatoMRR, inProvaMRR, payingCount, trialCount, exemptCount, total: tenants.length };
  }, [tenants, canoni]);

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ mb: 1.25 }}>
            <InterruttoreVista attiva="elenco" />
          </Box>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontWeight: 600,
              fontSize: { xs: '1.6rem', md: '1.95rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: tokens.color.graphite,
              fontVariationSettings: '"WONK" 0, "SOFT" 30',
            }}
          >
            Tenant
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 540 }}
          >
            Indice globale dei tenant della piattaforma. Filtra per tier o stato, apri il dettaglio per gestire moduli, billing e impersonare.
          </Typography>
        </Box>

        {/* Metriche header inline (denso ma chiaro) */}
        <Stack direction="row" spacing={3} useFlexGap alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
          <HeaderMetric
            label="Totale"
            value={aggregates.total.toString()}
          />
          <HeaderMetric
            label="Paganti"
            value={aggregates.payingCount.toString()}
            accentColor={tokens.color.tierPro.main}
          />
          <HeaderMetric
            label="In prova"
            value={aggregates.trialCount.toString()}
            accentColor={tokens.color.info}
          />
          <HeaderMetric
            label="Esenti"
            value={aggregates.exemptCount.toString()}
            accentColor={tokens.color.tierCustom.main}
          />
          <HeaderMetric
            label="MRR incassato"
            value={formatEUR(aggregates.incassatoMRR)}
            accentColor={tokens.color.tierBusiness.main}
            mono
          />
          <HeaderMetric
            label="MRR in prova"
            value={formatEUR(aggregates.inProvaMRR)}
            accentColor={tokens.color.tierBusiness.main}
            mono
            potenziale
          />
          <Tooltip title="Ricarica">
            <IconButton onClick={load} disabled={loading} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* FILTRI — barra inline tipo Linear */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          borderColor: tokens.color.line,
          bgcolor: tokens.color.paper,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            placeholder="Cerca per nome, P.IVA, email o ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: tokens.color.mist }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.85rem' },
            }}
          />
          <TextField
            select size="small" value={filterTier}
            onChange={(e) => setFilterTier(e.target.value as TenantTier | 'all')}
            sx={{ minWidth: 170, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            {TIER_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" value={filterLifecycle}
            onChange={(e) => setFilterLifecycle(e.target.value as any)}
            sx={{ minWidth: 180, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="only_active" sx={{ fontSize: '0.85rem' }}>Solo attivi</MenuItem>
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutti (incluso sospesi)</MenuItem>
            <MenuItem value="grace_period" sx={{ fontSize: '0.85rem' }}>Grace period</MenuItem>
            <MenuItem value="restricted" sx={{ fontSize: '0.85rem' }}>Restricted</MenuItem>
            <MenuItem value="suspended" sx={{ fontSize: '0.85rem' }}>Sospesi</MenuItem>
            <MenuItem value="canceled" sx={{ fontSize: '0.85rem' }}>Cancellati</MenuItem>
          </TextField>
          <TextField
            select size="small" value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as OnboardingStatus | 'all')}
            sx={{ minWidth: 170, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutti gli stati</MenuItem>
            {(Object.entries(STATUS_LABELS) as Array<[OnboardingStatus, string]>).map(([k, v]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: '0.85rem' }}>{v}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {/* TABELLA */}
      <Paper
        variant="outlined"
        sx={{
          borderColor: tokens.color.line,
          bgcolor: tokens.color.paper,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Table size="small" sx={{
            '& th': {
              fontFamily: tokens.font.body,
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tokens.color.mist,
              borderBottom: `1px solid ${tokens.color.line}`,
              py: 1.5,
            },
            '& td': {
              fontFamily: tokens.font.body,
              fontSize: '0.85rem',
              color: tokens.color.graphite,
              borderBottom: `1px solid ${tokens.color.lineSoft}`,
              py: 1.5,
            },
            '& tr:last-child td': { borderBottom: 0 },
            '& tbody tr': {
              transition: 'background-color 120ms ease',
              cursor: 'pointer',
            },
            '& tbody tr:hover': {
              backgroundColor: tokens.color.bone,
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Seat</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell align="right">MRR stima</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((t) => {
                const canone = canoni.get(t.id)?.canone ?? null;
                const incasso: IncassoDellAzienda | null = canoni.get(t.id)?.incasso ?? null;
                const mrr = canone ? canone.totale_centesimi / 100 : null;
                // Solo chi paga ha la cifra piena; il resto è potenziale (in prova) o fermo (ritardo, pausa, niente).
                const incassa = incasso?.stato === 'paga';
                const statoIncasso = incasso && mrr !== null && mrr > 0 ? testoIncasso(incasso) : null;
                const billingStatus = t.billing?.subscription_status ?? (t.tier === 'base' ? 'base' : undefined);
                return (
                  <TableRow
                    key={t.id}
                    onClick={() => navigate(`/tenants/${t.id}`)}
                    hover
                  >
                    {/* Tenant — nome bold + ID mono small */}
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}
                      >
                        {t.nome_azienda}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          mt: 0.25,
                          fontFamily: tokens.font.mono,
                          fontSize: '0.68rem',
                          color: tokens.color.mist,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {t.id.length > 14 ? `${t.id.slice(0, 8)}…${t.id.slice(-4)}` : t.id}
                      </Box>
                    </TableCell>

                    {/* Tier — badge editoriale */}
                    <TableCell>
                      <TierBadge tier={t.tier} exempt={t.billing?.exempt} size="sm" />
                    </TableCell>

                    {/* Seat — barra + contatore */}
                    <TableCell>
                      <SeatBar
                        used={t.seat?.used}
                        quota={t.seat?.quota}
                        exempt={t.billing?.exempt}
                      />
                    </TableCell>

                    {/* Billing */}
                    <TableCell>
                      {incasso?.stato === 'dimostrativa' ? (
                        <SoftChip label="Dimostrativa" tone="default" />
                      ) : t.billing?.exempt ? (
                        <SoftChip label="Esente" tone="info" />
                      ) : billingStatus ? (
                        <SoftChip
                          label={BILLING_STATUS_LABELS[billingStatus] ?? billingStatus}
                          tone={BILLING_STATUS_TONE[billingStatus] ?? 'default'}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: tokens.color.mist }}>—</Typography>
                      )}
                    </TableCell>

                    {/* MRR stima: il canone completo; i servizi sotto, il dettaglio passando sopra */}
                    <TableCell align="right">
                      <Tooltip
                        placement="left"
                        disableHoverListener={!canone || canone.righe.length === 0}
                        title={canone ? <DettaglioCanone canone={canone} compatto /> : ''}
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: tokens.color.paper,
                              color: tokens.color.graphite,
                              border: `1px solid ${tokens.color.line}`,
                              boxShadow: '0 8px 24px rgba(22, 22, 22, 0.12)',
                              p: 1.5,
                              maxWidth: 440,
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Box
                            component="span"
                            sx={{
                              fontFamily: tokens.font.mono,
                              fontSize: '0.85rem',
                              fontWeight: incassa && mrr && mrr > 0 ? 600 : 400,
                              color: mrr === null
                                ? tokens.color.mist
                                : incasso?.stato === 'in_ritardo'
                                  ? tokens.color.warning
                                  : incassa && mrr > 0
                                    ? tokens.color.graphite
                                    : tokens.color.ash,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {formatEUR(mrr)}
                          </Box>
                          {(statoIncasso || (canone && canone.aggiunte_centesimi > 0)) && (
                            <Box
                              component="span"
                              sx={{
                                mt: 0.25,
                                fontFamily: tokens.font.mono,
                                fontSize: '0.68rem',
                                color: incasso?.stato === 'in_ritardo' ? tokens.color.warning : tokens.color.mist,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {/* Chi non paga dice perché; chi paga mostra la parte dei servizi (il resto nel dettaglio). */}
                              {statoIncasso ?? `servizi ${euroDaCentesimi(canone?.aggiunte_centesimi ?? 0)}`}
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Onboarding status */}
                    <TableCell>
                      {t.onboarding_status ? (
                        <SoftChip
                          label={STATUS_LABELS[t.onboarding_status]}
                          tone={STATUS_TONE[t.onboarding_status]}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: tokens.color.mist }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Azioni */}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ pr: 2 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Apri dettaglio">
                          <IconButton size="small" onClick={() => navigate(`/tenants/${t.id}`)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Impersona (read-only 30min)">
                          <IconButton
                            size="small"
                            sx={{ color: tokens.color.tierBusiness.main }}
                            onClick={async () => {
                              try {
                                await startImpersonation(t.id);
                              } catch (e) {
                                console.error('Impersonation failed', e);
                              }
                            }}
                          >
                            <ImpersonateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina tenant (soft/hard)">
                          <IconButton
                            size="small"
                            sx={{ color: 'error.main' }}
                            onClick={() => handleOpenDeleteDialog(t)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography
                      sx={{
                        fontFamily: tokens.font.display,
                        fontSize: '1.05rem',
                        fontWeight: 500,
                        fontStyle: 'italic',
                        color: tokens.color.mist,
                      }}
                    >
                      Nessun tenant corrisponde ai filtri.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ====== DIALOG ELIMINAZIONE TENANT ====== */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleteDialog.submitting && setDeleteDialog(INITIAL_DELETE_STATE)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: `1px solid ${tokens.color.line}` } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5,
          fontFamily: tokens.font.display, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em',
        }}>
          <WarningIcon sx={{ color: tokens.color.danger }} />
          Elimina tenant
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Stai per eliminare il tenant <strong>{deleteDialog.tenant?.nome_azienda}</strong>
            <Typography component="span" sx={{ ml: 1, fontFamily: tokens.font.mono, fontSize: '0.75rem', color: tokens.color.mist }}>
              {deleteDialog.tenant?.id}
            </Typography>
          </DialogContentText>

          <RadioGroup
            value={deleteDialog.mode}
            onChange={(e) => setDeleteDialog((s) => ({ ...s, mode: e.target.value as 'soft' | 'hard', error: null }))}
          >
            <FormControlLabel
              value="soft"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Sospensione (soft delete)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Imposta tenant_status = 'suspended'. Login bloccato, dati intatti. <strong>Reversibile</strong>.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="hard"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    Eliminazione definitiva (hard delete)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cancella TUTTO: Firebase Auth users + tutti i documenti Firestore con tenant_id == X + sub-collections.
                    <strong> IRREVERSIBILE</strong>. Solo per test/cleanup.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          {deleteDialog.mode === 'hard' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Per confermare, scrivi esattamente il nome del tenant:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontFamily: 'monospace' }}>
                {deleteDialog.tenant?.nome_azienda}
              </Typography>
              <TextField
                fullWidth size="small"
                value={deleteDialog.confirmationName}
                onChange={(e) => setDeleteDialog((s) => ({ ...s, confirmationName: e.target.value, error: null }))}
                placeholder={deleteDialog.tenant?.nome_azienda}
                disabled={deleteDialog.submitting}
                autoFocus
              />
            </Box>
          )}

          {deleteDialog.error && (
            <Alert severity="error" sx={{ mt: 2 }}>{deleteDialog.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog(INITIAL_DELETE_STATE)}
            disabled={deleteDialog.submitting}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={
              deleteDialog.submitting
              || (deleteDialog.mode === 'hard' && deleteDialog.confirmationName.trim() !== deleteDialog.tenant?.nome_azienda.trim())
            }
            startIcon={deleteDialog.submitting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialog.submitting
              ? 'Eliminazione...'
              : deleteDialog.mode === 'hard' ? 'Elimina definitivamente' : 'Sospendi tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

// ============================================================================
// HEADER METRIC INLINE — pill compatta con label uppercase + valore display.
// ============================================================================

interface HeaderMetricProps {
  label: string;
  value: string;
  accentColor?: string;
  mono?: boolean;
  /** Cifra non ancora reale (es. canone delle prove gratuite): filetto tratteggiato e cifra attenuata. */
  potenziale?: boolean;
}

const HeaderMetric: React.FC<HeaderMetricProps> = ({ label, value, accentColor, mono = false, potenziale = false }) => (
  <Box sx={{ borderLeft: `2px ${potenziale ? 'dashed' : 'solid'} ${accentColor ?? tokens.color.line}`, pl: 1.5 }}>
    <Typography
      sx={{
        fontFamily: tokens.font.body,
        fontSize: '0.62rem',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.color.mist,
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: mono ? tokens.font.mono : tokens.font.display,
        fontSize: mono ? '0.95rem' : '1.15rem',
        fontWeight: 600,
        color: potenziale ? tokens.color.ash : accentColor ?? tokens.color.graphite,
        lineHeight: 1.2,
        mt: 0.25,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);
